import { TestLocators } from 'constants/Locators';

import { MUI, MUIIcons, MUILab, ThemeMode } from 'ci-esi-web-shared';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { notificationsSectionClick } from 'common/analytics';
import { colors } from 'styles/theme/colors';
import { ProductUpdateType, ProductUpdateTypeGA } from 'types/ProductUpdate';

import { NotificationsList } from '../NotificationsList';
import { selectActiveProductUpdateInWindow, selectScrollProductUpdateState } from '../selectors';
import { actions as notificationsActions } from '../slice';
import { ParsedProductUpdate } from '../types';
import { TabPanel } from './TabPanel';

const DIALOG_PAPER_WIDTH = 750;
const DIALOG_PAPER_HEIGHT = '90vh';
const TABS_HEIGHT = 48;
const CLOSE_BUTTON_WIDTH = 64;

export interface NotificationsWindowProps {
  isOpen: boolean;
  notifications: ParsedProductUpdate[];
  hasNewNotifications: boolean;
  onClose(): void;
  handleAllAsRead?: () => void;
  handleSelectNotificationsCategory: (category: ProductUpdateType) => void;
}

export const NotificationsWindow = ({
  isOpen,
  notifications,
  hasNewNotifications,
  onClose,
  handleAllAsRead,
  handleSelectNotificationsCategory,
}: NotificationsWindowProps) => {
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;

  const activeProductUpdateInWindow = useSelector(selectActiveProductUpdateInWindow);
  const scrollProductUpdateState = useSelector(selectScrollProductUpdateState);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [activeCategory, setActiveCategory] = useState(ProductUpdateType.Notification);
  const productUpdatesContainerRef = useRef<HTMLDivElement | null>(null);
  const notificationsContainerRef = useRef<HTMLDivElement | null>(null);
  const dataUpdatesContainerRef = useRef<HTMLDivElement | null>(null);

  const resetTabsPosition = useCallback(() => {
    productUpdatesContainerRef.current?.scrollTo(0, 0);
    notificationsContainerRef.current?.scrollTo(0, 0);
    dataUpdatesContainerRef.current?.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setActiveCategory(activeProductUpdateInWindow);
  }, [activeProductUpdateInWindow]);

  const getTabId = (type: ProductUpdateType) => type.replace(' ', '_');

  const handleImagesLoaded = useCallback(async () => {
    resetTabsPosition();
    const productUpdateTab = document.getElementById(getTabId(activeCategory));

    if (!scrollProductUpdateState?.date) return;
    const productUpdate = productUpdateTab?.getElementsByClassName(scrollProductUpdateState?.date)[0];

    if (productUpdateTab && productUpdate) {
      await Promise.all(
        Array.from(productUpdateTab?.querySelectorAll('img'))
          .filter((img: HTMLImageElement) => !img.complete)
          .map(
            (img) =>
              new Promise((resolve) => {
                img.addEventListener('load', resolve);
                img.addEventListener('error', resolve);
              }),
          ),
      );

      productUpdate.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
    }
  }, [scrollProductUpdateState?.date, activeCategory, resetTabsPosition]);

  useEffect(() => {
    handleImagesLoaded();
  }, [handleImagesLoaded]);

  const getTabIndex = useCallback(
    (type: ProductUpdateType) => Object.values(ProductUpdateType).findIndex((value) => value === type),
    [],
  );

  const getNotifications = useCallback(
    (category: ProductUpdateType) => {
      const filteredNotifications = notifications.filter(({ productupdatetype }) => productupdatetype === category);

      return (
        <MUI.Box data-testid={TestLocators.NotificationsPopUp_Content} sx={{ paddingBottom: '24px' }}>
          <NotificationsList
            notifications={filteredNotifications}
            handleSelectNotificationsCategory={handleSelectNotificationsCategory}
          />
        </MUI.Box>
      );
    },
    [notifications, handleSelectNotificationsCategory],
  );

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, index: string) => {
      resetTabsPosition();
      dispatch(notificationsActions.setScrollProductUpdateState(null));

      const type = Object.values(ProductUpdateType)[Number(index)];
      notificationsSectionClick(ProductUpdateTypeGA[type]);
      dispatch(notificationsActions.setActiveProductUpdateInWindow(type));
    },
    [dispatch, resetTabsPosition],
  );

  const renderTabs = useMemo(() => {
    const notificationsIndex = getTabIndex(ProductUpdateType.Notification).toString();
    const dataUpdateIndex = getTabIndex(ProductUpdateType.DataUpdate).toString();
    const productUpdateIndex = getTabIndex(ProductUpdateType.ProductUpdate).toString();

    return (
      <MUILab.TabContext value={String(getTabIndex(activeCategory))}>
        <MUI.Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <MUI.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <MUILab.TabList
              sx={{ '& .MuiTabs-indicator': { backgroundColor: colors.primary } }}
              variant="standard"
              onChange={handleChange}
            >
              <MUI.Tab
                data-testid={TestLocators.NotificationsPopUp_AllNotifications}
                label={t('LANDING_PAGE.NOTIFICATIONS.VIEW_ALL')}
                value={notificationsIndex}
              />
              <MUI.Tab
                data-testid={TestLocators.NotificationsPopUp_DataUpdates}
                label={t('LANDING_PAGE.DATA_UPDATES')}
                value={dataUpdateIndex}
              />
              <MUI.Tab
                data-testid={TestLocators.NotificationsPopUp_PlatformUpdates}
                label={t('LANDING_PAGE.PLATFORM_UPDATES')}
                value={productUpdateIndex}
              />
            </MUILab.TabList>
            {hasNewNotifications && (
              <MUI.Typography
                data-testid={TestLocators.NotificationsPopUp_MarkAllRead}
                variant="h5"
                onClick={handleAllAsRead}
                sx={{
                  cursor: 'pointer',
                  fontSize: 14,
                  color: isDarkMode ? colors.primaryWhite : colors.primaryBlue,
                  textDecoration: 'none',
                  fontWeight: 400,
                  marginRight: `calc(${CLOSE_BUTTON_WIDTH}px + 24px)`,
                }}
              >
                {t('LANDING_PAGE.NOTIFICATIONS.MARK_ALL')}
              </MUI.Typography>
            )}
            <MUI.Button
              data-testid={TestLocators.NotificationsPopUp_Close}
              sx={{ position: 'absolute', top: 0, right: 0, height: TABS_HEIGHT, width: CLOSE_BUTTON_WIDTH }}
              onClick={onClose}
            >
              <MUIIcons.Close />
            </MUI.Button>
          </MUI.Box>
          <TabPanel value={productUpdateIndex} key="product">
            <MUI.Box
              id={getTabId(ProductUpdateType.ProductUpdate)}
              ref={productUpdatesContainerRef}
              sx={{
                height: `calc(${DIALOG_PAPER_HEIGHT} - ${TABS_HEIGHT}px)`,
                paddingTop: '24px',
                overflowY: 'auto',
                paddingRight: '14px',
              }}
            >
              {getNotifications(ProductUpdateType.ProductUpdate)}
            </MUI.Box>
          </TabPanel>
          <TabPanel value={notificationsIndex} key="notifications">
            <MUI.Box
              id={getTabId(ProductUpdateType.Notification)}
              ref={notificationsContainerRef}
              sx={{
                height: `calc(${DIALOG_PAPER_HEIGHT} - ${TABS_HEIGHT}px)`,
                paddingTop: '24px',
                overflowY: 'auto',
                paddingRight: '14px',
              }}
            >
              {getNotifications(ProductUpdateType.Notification)}
            </MUI.Box>
          </TabPanel>
          <TabPanel value={dataUpdateIndex} key="data">
            <MUI.Box
              id={getTabId(ProductUpdateType.DataUpdate)}
              ref={dataUpdatesContainerRef}
              sx={{
                height: `calc(${DIALOG_PAPER_HEIGHT} - ${TABS_HEIGHT}px)`,
                paddingTop: '24px',
                overflowY: 'auto',
                paddingRight: '14px',
              }}
            >
              {getNotifications(ProductUpdateType.DataUpdate)}
            </MUI.Box>
          </TabPanel>
        </MUI.Box>
      </MUILab.TabContext>
    );
  }, [
    getTabIndex,
    activeCategory,
    handleChange,
    t,
    hasNewNotifications,
    handleAllAsRead,
    onClose,
    getNotifications,
    isDarkMode,
  ]);

  return (
    <MUI.Dialog
      data-testid={TestLocators.NotificationsPopUp}
      closeAfterTransition
      PaperProps={{
        sx: {
          width: DIALOG_PAPER_WIDTH,
          height: DIALOG_PAPER_HEIGHT,
          maxWidth: 'inherit',
          background: isDarkMode ? colors.grey56 : colors.primaryWhite,
        },
      }}
      onClose={onClose}
      open={isOpen}
    >
      <MUI.DialogContent sx={{ padding: '0 0 0 24px', '&:first-of-type': { paddingTop: 0 } }}>
        {renderTabs}
      </MUI.DialogContent>
    </MUI.Dialog>
  );
};
